import { Modal, RichTextEditor } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { convertFromRaw, RawDraftContentState } from "draft-js";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { ProjectType, TypeOfTemplate } from "../../../clients/graphqlTypes.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { SelectMultipleField } from "../../commons/form-fields/SelectMultipleField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { cleanHTML, draftStateToHtml } from "../../utils.js";
import { CategoryInput } from "../categories/CategoryInput.js";
import { useProjectTypes } from "../projects/useProjectTypes.js";
import { TemplateTypeInput } from "../templateTypes/TemplateTypeInput.js";
import { useTypeOfTemplates } from "./quote/useTypeOfTemplates.js";

export interface FormValues {
  title: string;
  description: string;
  product: { articleNumber: string; supplierId: string } | null;
  templateType: { id: string; title: string } | null;
  category: string | null;
  typeOfTemplate: TypeOfTemplate | null;
  typeOfProject: ProjectType[];
}

interface Props {
  title?: string;
  templateTitle?: string;
  handleClose: () => void;
  handleComplete: (values: FormValues) => Promise<void> | void;
  initialTemplateTypeId?: string;
}

export const CreateQuoteTemplateModal = ({
  title,
  templateTitle = "",
  handleClose,
  handleComplete,
  initialTemplateTypeId,
}: Props) => {
  const { t } = useTranslate([
    "QuoteCreate",
    "QuoteItem",
    "Global",
    "Templates",
  ]);

  const { typeOfTemplatesOptions } = useTypeOfTemplates();
  const { projectTypeOptions } = useProjectTypes();

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(
        t("Title", {
          ns: "Global",
        })
      )
      .required(),
    description: Yup.string().label(
      t("Description", {
        ns: "QuoteItem",
      })
    ),
    templateType: Yup.object({
      id: Yup.string().required(),
      title: Yup.string(),
    })
      .label(t("Template type", { ns: "Templates" }))
      .nullable(),
    category: Yup.string()
      .label(t("Template category", { ns: "Templates" }))
      .nullable(),
    typeOfTemplate: Yup.string()
      .label(t("Type of template", { ns: "Templates" }))
      .nullable(),
    typeOfProject: Yup.array()
      .of(Yup.string())
      .label(t("Type of project", { ns: "Templates" })),
  });

  const initialValues: FormValues = React.useMemo(
    () => ({
      title: templateTitle || "",
      description: "",
      product: null,
      category: null,
      templateType: initialTemplateTypeId
        ? { id: initialTemplateTypeId, title: "" }
        : null,
      typeOfTemplate: null,
      typeOfProject: [],
    }),
    [templateTitle, initialTemplateTypeId]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleComplete}
    >
      {(formikProps: FormikProps<FormValues>) => {
        return (
          <Modal
            title={title ?? t("New Quote Template", { ns: "QuoteCreate" })}
            maxWidth="sm"
            actionButtons={[
              {
                label: t("Cancel", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: {
                  variant: "text",
                  disabled: formikProps.isSubmitting,
                },
              },
              {
                label: t("Create Template", { ns: "QuoteCreate" }),
                buttonProps: {
                  type: "submit",
                  form: formId,
                  loading: formikProps.isSubmitting,
                  disabled: formikProps.isSubmitting || !formikProps.isValid,
                },
              },
            ]}
            handleClose={handleClose}
          >
            <Form id={formId}>
              <Stack direction="column" spacing={1}>
                <TemplateTypeInput
                  templateTypeId={formikProps.values.templateType?.id ?? null}
                  onChange={async templateType => {
                    await formikProps.setValues(values => ({
                      ...values,
                      templateType: templateType,
                      title: values.title || templateType?.title || "",
                    }));
                  }}
                  onClear={async () => {
                    await formikProps.setValues(values => ({
                      ...values,
                      templateType: null,
                    }));
                  }}
                  required={false}
                />
                <CategoryInput
                  categoryId={formikProps.values.category}
                  onChange={async category => {
                    await formikProps.setFieldValue("category", category.id);
                  }}
                  onClear={
                    formikProps.values.category
                      ? async () => {
                          await formikProps.setFieldValue("category", null);
                        }
                      : undefined
                  }
                  label={t("Template category", { ns: "Templates" })}
                  selectButtonLabel={t("Add template category", {
                    ns: "Templates",
                  })}
                  required={false}
                />

                <TextField
                  label={t("Title", { ns: "Global" })}
                  name="title"
                  required
                  autoFocus
                />
                <RichTextEditor
                  label={t("Description", { ns: "QuoteItem" })}
                  htmlContent={formikProps.values.description}
                  onChange={async (content: RawDraftContentState) => {
                    await formikProps.setFieldValue(
                      "description",
                      cleanHTML(draftStateToHtml(convertFromRaw(content)))
                    );
                  }}
                  saveButtonLabel={t("Save", { ns: "Global" })}
                  cancelButtonLabel={t("Cancel", { ns: "Global" })}
                />

                <SelectField
                  label={t("Type of template", { ns: "Templates" })}
                  name="typeOfTemplate"
                  options={typeOfTemplatesOptions}
                  clearable
                />

                <SelectMultipleField
                  label={t("Type of project", { ns: "Templates" })}
                  name="typeOfProject"
                  options={projectTypeOptions}
                  clearable
                />
              </Stack>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export interface CreateQuoteTemplateProcessRef {
  createQuoteTemplate: (templateTitle?: string) => Promise<FormValues | null>;
}

export const CreateQuoteTemplateProcess = React.forwardRef(
  (
    props: Omit<Props, "handleClose" | "handleComplete">,
    forwardedRef: React.Ref<CreateQuoteTemplateProcessRef>
  ) => {
    const [state, setState] = React.useState<{
      isOpen: boolean;
      templateTitle: string | null;
    }>({ isOpen: false, templateTitle: null });

    const resolveRef = React.useRef<
      ((values: FormValues | null) => void) | null
    >(null);

    React.useImperativeHandle(
      forwardedRef,
      () => ({
        createQuoteTemplate: (templateTitle?: string) =>
          new Promise<FormValues | null>(resolve => {
            resolveRef.current = resolve;
            setState({ isOpen: true, templateTitle: templateTitle ?? null });
          }),
      }),
      []
    );

    return state.isOpen ? (
      <CreateQuoteTemplateModal
        handleClose={() => {
          resolveRef.current?.(null);
          resolveRef.current = null;
          setState({ isOpen: false, templateTitle: null });
        }}
        handleComplete={values => {
          resolveRef.current?.(values);
          resolveRef.current = null;
          setState({ isOpen: false, templateTitle: null });
        }}
        {...props}
        templateTitle={state.templateTitle ?? props.templateTitle}
      />
    ) : null;
  }
);
